import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { useTransactionReport, useExportTransactionReport } from '../hooks/useTransactionReport';
import { useChartOfAccounts } from '../../chart-of-accounts/hooks/useChartOfAccounts';
import { useLocations } from '../hooks/useLocations';
import { TransactionReportFilterParams } from '../types/transaction-report.types';
import { AccountType } from '../types/credit-debit-tracker.types';

const TransactionReport: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const reportRef = useRef<HTMLDivElement>(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);

  // Filter states
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const defaultFromDate = new Date(today);
  const defaultToDate = new Date();
  defaultToDate.setHours(23, 59, 59, 999);

  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);
  const [typeFilter, setTypeFilter] = useState<AccountType | ''>('');
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ id: string; name: string } | null>(null);

  // Build filter params
  const filterParams: TransactionReportFilterParams = {
    fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : undefined,
    toDate: toDate ? format(toDate, 'yyyy-MM-dd') : undefined,
    type: typeFilter || undefined,
    chartOfAccountId: selectedAccount?.id,
    location: selectedLocation?.name,
  };

  // Fetch data
  const { data: reportData, isLoading, error, refetch } = useTransactionReport(filterParams);
  const { exportReport } = useExportTransactionReport();

  // Fetch chart of accounts for filter
  const { chartOfAccounts } = useChartOfAccounts({
    take: 1000,
    journalEntry: true, // Only show accounts that allow journal entries
  });

  // Fetch locations for filter
  const { locations } = useLocations();

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTypeFilterChange = (event: SelectChangeEvent) => {
    setTypeFilter(event.target.value as AccountType | '');
    setPage(0);
  };

  const handleExportCSV = async () => {
    try {
      await exportReport(filterParams);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting CSV. Please try again.');
    }
  };

  const handleDownloadPDF = async (includeAllData = false) => {
    if (!reportRef.current || !reportData) return;
    
    try {
      let originalRowsPerPage = rowsPerPage;
      let originalPage = page;
      
      if (includeAllData) {
        setRowsPerPage(reportData.data.length);
        setPage(0);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Transaction Report', 20, 20);
      
      // Date range
      const fromDateStr = fromDate ? format(fromDate, 'dd/MM/yyyy') : 'N/A';
      const toDateStr = toDate ? format(toDate, 'dd/MM/yyyy') : 'N/A';
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${fromDateStr} to ${toDateStr}`, 20, 30);
      
      // Add line separator
      pdf.line(20, 35, 190, 35);
      
      // Create canvas from the report content
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      if (includeAllData) {
        setRowsPerPage(originalRowsPerPage);
        setPage(originalPage);
      }
      
      // Add report content to PDF
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 170;
      const startY = 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 20, startY, imgWidth, imgHeight);
      
      // Generate filename
      const fromDateFile = fromDate ? format(fromDate, 'yyyy-MM-dd') : 'unknown';
      const toDateFile = toDate ? format(toDate, 'yyyy-MM-dd') : 'unknown';
      const dataType = includeAllData ? 'Complete' : 'CurrentPage';
      const filename = `TransactionReport_${dataType}_${fromDateFile}_to_${toDateFile}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const clearFilters = () => {
    const now = new Date();
    const localStart = new Date(now);
    localStart.setHours(0, 0, 0, 0);
    const localEnd = new Date(now);
    localEnd.setHours(23, 59, 59, 999);
    
    setFromDate(localStart);
    setToDate(localEnd);
    setTypeFilter('');
    setSelectedAccount(null);
    setSelectedLocation(null);
    setPage(0);
  };

  const getTypeColor = (type: AccountType) => {
    return type === AccountType.CREDIT ? 'success' : 'primary';
  };

  const transactions = reportData?.data || [];
  const summary = reportData?.summary || {
    totalTransactions: 0,
    totalCredit: 0,
    totalDebit: 0,
    netAmount: 0,
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading transaction report: {error.message}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Transaction Report
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          View all credit and debit transactions with summary totals
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <div ref={reportRef}>
              <Grid container spacing={2}>
                {/* First Row: Dates, Type, Account */}
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={(date) => setFromDate(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={(date) => setToDate(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Transaction Type</InputLabel>
                    <Select
                      value={typeFilter}
                      label="Transaction Type"
                      onChange={handleTypeFilterChange}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value={AccountType.CREDIT}>Credit</MenuItem>
                      <MenuItem value={AccountType.DEBIT}>Debit</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={chartOfAccounts?.map(acc => ({ id: acc.id, name: `${acc.accountCode} - ${acc.name}` })) || []}
                    value={selectedAccount}
                    onChange={(_, value) => setSelectedAccount(value)}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Chart of Account" size="small" fullWidth />
                    )}
                  />
                </Grid>

                {/* Second Row: Location, Action Buttons */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={locations?.map((loc: any) => ({ id: loc.id, name: loc.name })) || []}
                    value={selectedLocation}
                    onChange={(_, value) => setSelectedLocation(value)}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option?.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return option.id === value.id;
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Location" size="small" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={9} display="flex" justifyContent="flex-end" alignItems="center">
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportCSV}
                    >
                      Export CSV
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<PictureAsPdfIcon />}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                    >
                      Download PDF
                    </Button>
                    <Menu
                      anchorEl={pdfMenuAnchor}
                      open={Boolean(pdfMenuAnchor)}
                      onClose={() => setPdfMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => { handleDownloadPDF(false); setPdfMenuAnchor(null); }}>
                        <ListItemIcon>
                          <PictureAsPdfIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Current Page Only" />
                      </MenuItem>
                      <MenuItem onClick={() => { handleDownloadPDF(true); setPdfMenuAnchor(null); }}>
                        <ListItemIcon>
                          <PictureAsPdfIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Complete Report" />
                      </MenuItem>
                    </Menu>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<RefreshIcon />}
                      onClick={() => refetch()}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              {/* Summary Cards */}
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {summary.totalTransactions}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Transactions
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="success.main">
                          ₹{summary.totalCredit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Credit
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          ₹{summary.totalDebit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Debit
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography 
                          variant="h6" 
                          color={summary.netAmount >= 0 ? "success.main" : "error.main"}
                        >
                          ₹{summary.netAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Net Amount (Credit - Debit)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Transactions Table */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Account Code</TableCell>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No transactions found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((transaction) => (
                          <TableRow key={transaction.id} hover>
                            <TableCell>
                              {format(new Date(transaction.date), 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {transaction.chartOfAccountCode}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {transaction.chartOfAccountName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={transaction.type}
                                color={getTypeColor(transaction.type as AccountType)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                ₹{transaction.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {transaction.description || ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {typeof transaction.location === 'string' 
                                  ? transaction.location 
                                  : transaction.location?.name || ''
                                }
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={transactions.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </div>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default TransactionReport;
